import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, ArrowLeft, Phone, FileText, MapPin, CheckCircle, AlertCircle, Check, X } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import BabiDocLogo from '../components/BabiDocLogo';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'patient',
    phone_number: '',
    address: '',
    order_number: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingEmailVerification, setPendingEmailVerification] = useState(false);

  // Password validation rules
  const passwordRules = [
    { label: '8 caractères minimum', test: (p) => p.length >= 8 },
    { label: '1 majuscule', test: (p) => /[A-Z]/.test(p) },
    { label: '1 minuscule', test: (p) => /[a-z]/.test(p) },
    { label: '1 chiffre', test: (p) => /\d/.test(p) },
    { label: '1 caractère spécial (!@#$%^&*)', test: (p) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(p) },
  ];

  const isPasswordValid = passwordRules.every(rule => rule.test(formData.password));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('Le mot de passe ne respecte pas tous les critères de sécurité');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      
      // Check if email verification is required
      if (response.data.pending_email_verification) {
        setPendingEmailVerification(true);
        toast.success('Un email de vérification a été envoyé !');
      } else if (response.data.token) {
        // Normal login (should not happen with new flow)
        login(response.data.token, response.data.user);
        toast.success('Inscription réussie!');
        
        if (response.data.user.role === 'doctor') {
          navigate('/doctor-dashboard');
        } else {
          navigate('/patient-dashboard');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  // Show success message for email verification pending
  if (pendingEmailVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full blur-3xl opacity-30"></div>
          <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-gradient-to-tr from-emerald-100 to-teal-100 rounded-full blur-3xl opacity-30"></div>
        </div>

        <Card className="w-full max-w-md card" data-testid="pending-verification-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center">
                <Mail className="w-10 h-10 text-teal-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Vérifiez votre email</CardTitle>
          </CardHeader>
          
          <CardContent className="text-center space-y-4">
            <div className="bg-teal-50 border-l-4 border-teal-400 p-4 rounded-r-lg">
              <p className="text-teal-800 font-medium">
                Un email de vérification a été envoyé à votre adresse.
              </p>
            </div>
            
            <p className="text-gray-600">
              Cliquez sur le lien dans l'email pour activer votre compte.
              {formData.role === 'doctor' && " Votre inscription sera ensuite examinée par notre équipe."}
            </p>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500">
                Email envoyé à: <strong>{formData.email}</strong>
              </p>
            </div>
            
            <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-r-lg text-left">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Le lien expire dans 48 heures. Vérifiez aussi vos spams.
              </p>
            </div>
            
            <div className="pt-4 space-y-3">
              <Button
                onClick={() => navigate('/login')}
                className="w-full btn-primary h-12"
                data-testid="go-login-btn"
              >
                Aller à la connexion
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full h-12"
                data-testid="go-home-btn"
              >
                Retour à l'accueil
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Back to home button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors z-10"
        data-testid="back-home-btn"
      >
        <ArrowLeft className="w-6 h-6" />
        <span className="text-sm font-medium">Accueil</span>
      </button>

      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-gradient-to-tr from-emerald-100 to-teal-100 rounded-full blur-3xl opacity-30"></div>
      </div>

      <Card className="w-full max-w-md card" data-testid="register-form">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_babidoc-health/artifacts/7a0rukid_HUMMM0fc84adc-81cc-4751-9a5e-2c9327247823.png" 
              alt="Côte d'Ivoire" 
              className="w-20 h-20 object-cover rounded-full"
            />
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">Rejoignez BabiDoc</CardTitle>
          <p className="text-gray-600 mt-2">Créez votre compte</p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Je suis un(e)</label>
              <RadioGroup value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })} data-testid="role-select">
                <div className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <RadioGroupItem value="patient" id="patient" data-testid="role-patient" />
                  <Label htmlFor="patient" className="cursor-pointer flex-1">Patient</Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <RadioGroupItem value="doctor" id="doctor" data-testid="role-doctor" />
                  <Label htmlFor="doctor" className="cursor-pointer flex-1">Médecin</Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  required
                  placeholder="Jean Dupont"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="pl-12 h-12"
                  data-testid="fullname-input"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="email"
                  required
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-12 h-12"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro WhatsApp
                <span className="text-gray-400 font-normal ml-1">(pour les notifications)</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="tel"
                  placeholder="+225 07 XX XX XX XX"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  className="pl-12 h-12"
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Champ Adresse */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse
                <span className="text-gray-400 font-normal ml-1">(optionnel)</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Votre adresse"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="pl-12 h-12"
                  data-testid="address-input"
                />
              </div>
            </div>

            {/* Champ Numéro d'Ordre (uniquement pour les médecins) */}
            {formData.role === 'doctor' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro d'inscription à l'Ordre des Médecins
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <FileText className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    required
                    placeholder="Ex: 12345-CI"
                    value={formData.order_number}
                    onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                    className="pl-12 h-12"
                    data-testid="order-number-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Ce numéro sera vérifié par notre équipe avant validation de votre compte
                </p>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-12 pr-12 h-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              
              {/* Password strength indicator */}
              {formData.password && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-600 mb-2">Critères de sécurité :</p>
                  <div className="grid grid-cols-2 gap-1">
                    {passwordRules.map((rule, index) => (
                      <div key={index} className="flex items-center gap-1 text-xs">
                        {rule.test(formData.password) ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <X className="w-3 h-3 text-red-400" />
                        )}
                        <span className={rule.test(formData.password) ? 'text-green-600' : 'text-gray-500'}>
                          {rule.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {formData.role === 'doctor' && (
              <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-r-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Votre inscription sera vérifiée par notre équipe avant activation.
                  Vous recevrez un email de confirmation.
                </p>
              </div>
            )}
            
            <Button
              type="submit"
              disabled={loading || !isPasswordValid}
              className="w-full btn-primary h-12"
              data-testid="register-btn"
            >
              {loading ? 'Création du compte...' : 'Créer un Compte'}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Vous avez déjà un compte?{' '}
              <Link to="/login" className="text-teal-600 hover:text-teal-700 font-semibold" data-testid="login-link">
                Connectez-vous
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;